<script setup>
  import { ref, computed, defineAsyncComponent, watch, getCurrentInstance } from 'vue'

  const BaseButton = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BaseButton.vue" */ '@/components/base/buttons/BaseButton.vue')
  )
  const RateIsChangedModal = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/modals/RateIsChanged.vue" */ '@/components/modals/RateIsChanged.vue')
  )
  const BaseLoader = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/BaseLoader.vue" */ '@/components/base/BaseLoader.vue')
  )
  const Success = defineAsyncComponent(() =>
      import(/* webpackChunkName: "views/cards/CardPage/Verification/statusBodies/Icons/Success.vue" */ '@/views/cards/CardPage/Verification/statusBodies/Icons/Success.vue')
  )
  const Failed = defineAsyncComponent(() =>
      import(/* webpackChunkName: "views/cards/CardPage/Verification/statusBodies/Icons/Error.vue" */ '@/views/cards/CardPage/Verification/statusBodies/Icons/Error.vue')
  )

  // store imports
  import { useRoute, useRouter } from "vue-router";
  const route = useRoute()
  const router = useRouter()

  import { useModalsStore } from '@/store/stores/modals'
  const modalsStore = useModalsStore()

  import { useStore } from 'vuex'
  const vuexStore = useStore()

  // constants
  const instance = getCurrentInstance();
  const text = instance.proxy.text

  const errorMessage = ref('')
  const fromAmount = ref('0')
  const toAmount = ref('0')
  const fixedRateString = ref(null)
  const status = ref(null)
  const updateRateInterval = ref(null)
  const exchangeAccountSelectOpened = ref(false)
  const exchangeAccountSelectSide = ref('from')

  // computed
  const swapNotAllowed = computed(() => {
    return !exchangeFromAccount.value || !exchangeToAccount.value
  })

  const exchangeDisabled = computed(() => {
    return !Number(fromAmount.value) || !Number(toAmount.value) || !exchangeRateString.value ||
        parseFloat(fromAmount.value) > exchangeFromAccount.value.balance || status.value === 'pending'
  })

  const statusTitle = computed(() => {
    if (status.value === 'pending') {
      return text('exchange.status_modal.title.pending')
    } else if (status.value === 'success') {
      return text('exchange.status_modal.title.success')
    } else {
      return text('exchange.status_modal.title.error')
    }
  })

  const statusInfo = computed(() => {
    if (status === 'pending') {
      return this.text('exchange.status_modal.dont_close')
    } else if (status === 'error') {
      return this.text('exchange.status_modal.try_again_later')
    }
  })

  const isRateChanged = computed(() => {
    return exchangeRateString.value !== fixedRateString.value
  })

  const exchangeRateString = computed(() => {
    if (!rateObj.value) {
      return ''
    }

    return `${Number(rateObj.value.quantity_in)} ${rateObj.value.from_currency_code} = ${Number(rateObj.value.quantity_out)} ${rateObj.value.to_currency_code}`;
  })

  const accountsIsLoading = computed(() => {
    return vuexStore.state.accountsLoading
  })

  const exchangeFromAccount = computed(() => {
    return vuexStore.state.exchangeFromAccount
  })

  const exchangeToAccount = computed(() => {
    return vuexStore.state.exchangeToAccount
  })

  const exchangeRates = computed(() => {
    return vuexStore.state.exchangeRates
  })

  const walletList = computed(() => {
    return vuexStore.state.walletList || []
  })
  const routeAccountId = computed(() => {
    return route.query.account_id ? String(route.query.account_id) : ''
  })

  const exchangeAccountSelectTitle = computed(() => {
    return exchangeAccountSelectSide.value === 'from'
      ? text('select_exchange.from')
      : text('select_exchange.to')
  })

  const availableExchangeAccounts = computed(() => {
    return walletList.value
  })

  const rateObj = computed(() => {
    const from = exchangeFromAccount.value?.currency_code
    const to = exchangeToAccount.value?.currency_code

    if (!exchangeRates.value?.rates) {
      return ''
    }

    return exchangeRates.value.rates.find(r => r.from_currency_code === from && r.to_currency_code === to)
  })

  // watchers
  watch(exchangeRateString,(v) => {
    if (!fixedRateString.value && v) {
      fixedRateString.value = v
    }
  })

  // methods
  function tryAgain() {
    if (!updateRateInterval.value) {
      refreshExchangeRates()
    }
    modalsStore.reset()
    status.value = null
  }
  function goToHomePage() {
    router.push({ name: 'Home' })
  }
  function refreshExchangeRates () {
    vuexStore.dispatch('refreshExchangeRates')

    updateRateInterval.value = setInterval(() => {
      vuexStore.dispatch('refreshExchangeRates')
    }, 30000)
  }
  function openExchangeAccountSelect(side) {
    exchangeAccountSelectSide.value = side
    exchangeAccountSelectOpened.value = true
  }
  function closeExchangeAccountSelect() {
    exchangeAccountSelectOpened.value = false
  }
  function selectExchangeAccount(account) {
    if (exchangeAccountSelectSide.value === 'from') {
      if (account.id === exchangeToAccount.value?.id) {
        vuexStore.commit("updateExchangeToAccount", exchangeFromAccount.value)
      }
      vuexStore.commit("updateExchangeFromAccount", account)
    } else {
      if (account.id === exchangeFromAccount.value?.id) {
        vuexStore.commit("updateExchangeFromAccount", exchangeToAccount.value)
      }
      vuexStore.commit("updateExchangeToAccount", account)
    }

    fixedRateString.value = null
    updateFromAmount({ data: fromAmount.value })
    closeExchangeAccountSelect()
  }
  function selectExchangeOpenedFrom() {
    openExchangeAccountSelect('from')
  }
  function selectExchangeOpenedTo() {
    openExchangeAccountSelect('to')
  }
  function changeExchangeAccounts() {
    const el = document.querySelector('.change_btn')
    el.classList.toggle('spin')

    const tmpAmount = fromAmount.value
    fromAmount.value = toAmount.value
    toAmount.value = tmpAmount

    const tmpAccount = exchangeFromAccount.value;
    vuexStore.commit("updateExchangeFromAccount", exchangeToAccount.value);
    vuexStore.commit("updateExchangeToAccount", tmpAccount);

    fixedRateString.value = exchangeRateString.value
  }
  function setMax() {
    if (Number(exchangeFromAccount.value.balance) > 0) {
      fromAmount.value = exchangeFromAccount.value.balance
      updateFromAmount()
    }
  }
  function getExchangeRate () {
    if (!rateObj.value || parseFloat(rateObj.value.quantity_in) !== 1) {
      return 0
    } else {
      return rateObj.value.quantity_out
    }
  }
  function normalizeAmountInput(value, event) {
    const normalizedValue = String(value ?? '')
    const inputDigit = event?.data

    if (
      normalizedValue.length > 1 &&
      normalizedValue[0] === '0' &&
      normalizedValue[1] !== '.' &&
      inputDigit
    ) {
      return inputDigit
    }

    return normalizedValue || '0'
  }
  function updateFromAmount(event) {
    fromAmount.value = normalizeAmountInput(fromAmount.value, event)

    if (!fromAmount.value) {
      fromAmount.value = 0
    }

    const rate = getExchangeRate();
    toAmount.value = Math.round(rate * parseFloat(fromAmount.value) * 100) / 100;
  }
  function updateToAmount(event) {
    toAmount.value = normalizeAmountInput(toAmount.value, event)

    if (!toAmount.value) {
      toAmount.value = 0
    }

    const rate = getExchangeRate();
    fromAmount.value = rate
      ? Math.round((parseFloat(toAmount.value) / rate) * 100) / 100
      : 0;
  }
  function recalculateAmount() {
    fixedRateString.value = exchangeRateString.value
    updateFromAmount(fromAmount.value)
  }
  async function doExchange() {
    if (isRateChanged.value && getExchangeRate()) {
      modalsStore.setModalName('RateIsChanged')
      return
    }

    errorMessage.value = ''
    status.value = 'pending'
    clearInterval(updateRateInterval.value)
    updateRateInterval.value = null

    const response = await vuexStore.dispatch("doExchange", {
      from_account_id: exchangeFromAccount.value.id,
      to_account_id: exchangeToAccount.value.id,
      from_amount: fromAmount.value.toString(),
      to_amount: toAmount.value.toString(),
      rate_id: rateObj.value.id,
    })
    if (response?.wallet_transaction_id) {
      router.replace({
        name: 'ExchangeTransaction',
        params: { id: response.wallet_transaction_id },
      })
      return
    }

    status.value = null
    errorMessage.value = response?.message || text('exchange.status_modal.try_again_later')
  }

  vuexStore.dispatch('setDefaultExchangeAccounts', {
    from_account_id: routeAccountId.value,
  })
  refreshExchangeRates()
</script>

<template>
  <div class="exchange">
    <template v-if="!status">
      <div class="exchange_head">
        <button @click="$router.go(-1)">
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18.5L9 12.5L15 6.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <h2>{{text('exchange.title')}}</h2>
      </div>
      <div class="exchange_block">
        <BaseLoader v-if="accountsIsLoading" />
        <template v-else-if="swapNotAllowed">
          <p>{{ text('exchange.not_available') }}</p>
        </template>
        <template v-else>
          <div class="exchange_card">
            <div class="exchange_card__top">
              <h4>{{text('exchange.from')}}</h4>
              <p :class="{ exceed: fromAmount > exchangeFromAccount.balance}">
                {{text('exchange.current_balance')}}
                <span>{{exchangeFromAccount.balance}}</span>
                <span @click="setMax" class="text-accent">MAX</span>
              </p>
            </div>
            <div class="exchange_card__bottom">
              <button @click="selectExchangeOpenedFrom">
                <span>{{exchangeFromAccount.currency_code}}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.30078 4.16602L12.181 8.78943C12.875 9.44681 12.875 10.5519 12.1811 11.2093L7.30078 15.8327" stroke="#A6A4B4" stroke-width="1.25" stroke-linecap="round"/>
                </svg>
              </button>
              <div class="money-inp__wrap">
                <input
                    v-model="fromAmount"
                    @input="updateFromAmount"
                    data-maska="9 99#.##"
                    class="money-inp"
                >
                <span>{{getCurrencySymbol(exchangeFromAccount.currency_code)}}</span>
              </div>
            </div>
          </div>
          <button @click="changeExchangeAccounts" class="change_btn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.93 16.6663V3.33301M14.93 3.33301L18.7507 7.15367M14.93 3.33301L11.1094 7.15367" stroke="black" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5.90465 3.33268V16.666M5.90465 16.666L9.72531 12.8454M5.90465 16.666L2.08398 12.8454" stroke="black" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="exchange_card">
            <div class="exchange_card__top">
              <h4>{{text('exchange.to')}}</h4>
              <p>
                {{text('exchange.current_balance')}}
                <span>{{exchangeToAccount.balance}}</span>
              </p>
            </div>
            <div class="exchange_card__bottom">
              <button @click="selectExchangeOpenedTo">
                <span>{{exchangeToAccount.currency_code}}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.30078 4.16602L12.181 8.78943C12.875 9.44681 12.875 10.5519 12.1811 11.2093L7.30078 15.8327" stroke="#A6A4B4" stroke-width="1.25" stroke-linecap="round"/>
                </svg>
              </button>
              <div class="money-inp__wrap">
                <input
                    v-model="toAmount"
                    @input="updateToAmount"
                    data-maska="9 99#.##"
                    class="money-inp"
                >
                <span>{{getCurrencySymbol(exchangeToAccount.currency_code)}}</span>
              </div>
            </div>
            <div class="line" />
            <div class="exchange_card__foot">
              {{ fixedRateString || text('exchange.no_rate') }}
            </div>
          </div>
        </template>
      </div>
      <div class="error">{{ errorMessage }}</div>
      <BaseButton
          v-if="exchangeFromAccount && exchangeToAccount"
          class="submit-btn"
          @click="doExchange"
          :disabled="exchangeDisabled"
      >
        <template #label>
          {{ text('exchange.submit_btn') }}
        </template>
      </BaseButton>
      <RateIsChangedModal
          :rate-string="exchangeRateString"
          @refresh="recalculateAmount"
          @close="tryAgain"
      />
      <div
        v-if="exchangeAccountSelectOpened"
        class="exchange-account-select"
        @click.self="closeExchangeAccountSelect"
      >
        <div class="exchange-account-select__content">
          <div class="exchange-account-select__head">
            <div>
              <span>{{ text('select_exchange.select_account') }}</span>
              <strong>{{ exchangeAccountSelectTitle }}</strong>
            </div>
            <button type="button" @click="closeExchangeAccountSelect">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="exchange-account-select__list">
            <button
              v-for="account in availableExchangeAccounts"
              :key="account.id"
              type="button"
              class="exchange-account-select__item"
              :class="{
                active: account.id === (
                  exchangeAccountSelectSide === 'from'
                    ? exchangeFromAccount?.id
                    : exchangeToAccount?.id
                )
              }"
              @click="selectExchangeAccount(account)"
            >
              <span>{{ account.currency_code }}</span>
              <strong>{{ getCurrencySymbol(account.currency_code) }} {{ account.balance }}</strong>
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="exchange-status">
        <div class="status-icon-wrapper">
          <BaseLoader v-if="status === 'pending'" />
          <Success v-else-if="status === 'success'" />
          <Failed v-else-if="status === 'error'" />
        </div>
        <div class="title">
          {{ statusTitle }}
        </div>
        <div v-if="statusInfo" class="status-info">
          {{ statusInfo }}
        </div>
        <div class="exchange-info">
          <div class="row">
            <div class="label">
              {{ text('exchange.status_modal.info.summary') }}
            </div>
            <div class="value">
                  <span class="from">
                    {{ fromAmount }} {{getCurrencySymbol(exchangeFromAccount.currency_code)}}
                  </span>
              <span class="to">
                    ({{ toAmount }} {{getCurrencySymbol(exchangeToAccount.currency_code)}})
                  </span>
            </div>
          </div>
          <div class="row">
            <div class="label">
              {{ text('exchange.status_modal.info.rate') }}
            </div>
            <div class="value">
              {{ fixedRateString }}
            </div>
          </div>
          <div class="row">
            <div class="label">
              {{ text('exchange.status_modal.info.date') }}
            </div>
            <div class="value">
              {{ formatDateTime(new Date()) }}
            </div>
          </div>
        </div>

        <div class="actions">
          <BaseButton
              v-if="status === 'error'"
              @click="tryAgain"
          >
            <template #label>
              {{ text('common.actions.try_again') }}
            </template>
          </BaseButton>
          <BaseButton
              grayscale
              @click="goToHomePage"
          >
            <template #label>
              {{ text('common.actions.close') }}
            </template>
          </BaseButton>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.exchange {
  min-height: 600px;
  .exchange-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;

    .status-icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 180px;
    }
    .title {
      font-size: 20px;
      font-weight: 600;
      text-align: center;
    }
    .status-info {
      font-size: 16px;
      text-align: center;
    }
    .exchange-info {
      width: 100%;

      display: flex;
      flex-direction: column;
      gap: 12px;

      padding: 16px;
      margin-top: 24px;
      border-radius: 16px;
      background: var(--element-bg);

      .row {
        display: flex;
        align-items: center;
        justify-content: space-between;

        font-size: 16px;
        font-weight: 400;
        .label {
          color: var(--text-color-secondary);
        }
        .value {
          display: flex;
          align-items: center;
          gap: 8px;

          color: var(--text-color-primary);

          .from {
            font-size: 18px;
            font-weight: 500;
          }
          .to {
            color: var(--text-color-secondary);
          }
        }
      }
    }

    .actions {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-top: 64px;
    }
  }

  .exchange-account-select {
    position: fixed;
    inset: 0;
    z-index: 20;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 24px;
    background: rgba(0, 0, 0, 0.5);
  }

  .exchange-account-select__content {
    width: 100%;
    max-width: 520px;
    border-radius: 16px;
    background: var(--element-bg);
    padding: 20px;
  }

  .exchange-account-select__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;

    div {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    span {
      color: var(--text-color-secondary);
      font-size: 14px;
      line-height: 135%;
    }

    strong {
      color: var(--text-color-primary);
      font-size: 20px;
      line-height: 125%;
    }

    button {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--border-color);
      border-radius: 50%;
      background: var(--element-bg-accent);
      color: var(--text-color-secondary);
      cursor: pointer;
    }
  }

  .exchange-account-select__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: min(420px, 55vh);
    overflow-y: auto;
  }

  .exchange-account-select__item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--element-bg-accent);
    color: var(--text-color-primary);
    padding: 14px 16px;
    cursor: pointer;

    span {
      font-size: 18px;
      font-weight: 700;
    }

    strong {
      color: var(--text-color-secondary);
      font-size: 16px;
      font-weight: 500;
    }

    &.active {
      border-color: var(--accent-bg);
    }
  }
}
</style>
